import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseMemberProps } from "@custom-types/member-types";
import { BaseProjectProps } from "@custom-types/project-types";
import { DeleteIcon, Dialog, MenuItem, Text, useToast } from "@flat-ui";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { UserDisplay } from "@components/common/user-display";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";
import {
  TableType,
  ProjectMemberEvents,
  WorkspaceMemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { isTeam } from "@utils/team-utils";
import { useState } from "react";

/** Determines from where a member should be removed */
type RemoveTarget = "project" | "workspace";

interface Props extends BaseCompanyIdProps, BaseMemberProps, BaseProjectProps {
  /** Callback to close the parent 3-dot menu */
  onMenuClose: () => void;

  /** Whether the user can remove the member from the workspace */
  canRemoveFromWorkspace: boolean;
}

/** Menu button for removing a member from a project or workspace */
export function ProjectRemoveMenuButton({
  companyId,
  project: selectedProject,
  member,
  onMenuClose,
  canRemoveFromWorkspace,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);

  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const { isRemovingProjectMember } = useAppSelector(
    fetchingProjectsFlagsSelector
  );
  const { isRemovingCompanyMember } = useAppSelector(
    fetchingMembersFlagsSelector
  );

  const isRemoving =
    removeTarget === "project"
      ? isRemovingProjectMember
      : isRemovingCompanyMember;

  function closeDialog(): void {
    setRemoveTarget(null);
    onMenuClose();
  }

  async function confirmRemove(): Promise<void> {
    switch (removeTarget) {
      case "project":
        await removeFromProject();
        break;
      case "workspace":
        await removeFromWorkspace();
        break;
    }
  }

  function trackRemoveMemberEvent(target: RemoveTarget): void {
    trackEvent({
      name:
        target === "project"
          ? ProjectMemberEvents.removeMember
          : WorkspaceMemberEvents.removeMember,
      props: {
        dataType: TableType.projectMembers,
        eventLocation: "project-member-menu",
        removeTarget: target,
      },
    });
  }

  async function removeFromProject(): Promise<void> {
    trackRemoveMemberEvent("project");

    try {
      await dispatch(
        removeMemberFromProject({
          companyId,
          projectId: selectedProject.id,
          member,
        })
      ).unwrap();

      openToast({
        title: "Member removed from project",
        message: (
          <RemoveMemberSuccessDescription
            member={member}
            subjectType="project"
            entityName={selectedProject.name}
          />
        ),
      });
    } finally {
      closeDialog();
    }
  }

  async function removeFromWorkspace(): Promise<void> {
    trackRemoveMemberEvent("workspace");

    try {
      await dispatch(removeMemberFromCompany({ companyId, member })).unwrap();

      openToast({
        title: "Member removed from workspace",
        message: (
          <RemoveMemberSuccessDescription
            member={member}
            subjectType="workspace"
            entityName={companyName ?? ""}
          />
        ),
      });
    } finally {
      closeDialog();
    }
  }

  const isMemberTeam = isTeam(member);
  const entityLabel = isMemberTeam ? "team" : "member";

  const removeTargetDisplayName =
    removeTarget === "project" ? "project" : "workspace";

  return (
    <>
      {isMemberTeam || !canRemoveFromWorkspace ? (
        <MenuItem
          label={"Remove from project"}
          Icon={DeleteIcon}
          onClick={() => setRemoveTarget("project")}
        />
      ) : (
        <MenuItem
          label="Remove member"
          Icon={DeleteIcon}
          subMenuTrigger="click"
        >
          <MenuItem
            label="From project"
            onClick={() => setRemoveTarget("project")}
          />
          <MenuItem
            label="From workspace"
            onClick={() => setRemoveTarget("workspace")}
          />
        </MenuItem>
      )}

      <Dialog
        title={`Remove from ${removeTargetDisplayName}`}
        confirmText="Remove"
        open={removeTarget !== null}
        onConfirm={confirmRemove}
        onCancel={closeDialog}
        showSpinner={isRemoving}
      >
        <Text
          variant="bodyM"
          sx={{
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          When removing {entityLabel} from {removeTargetDisplayName} they will
          no longer be able to access this {removeTargetDisplayName}. You can
          always invite this {entityLabel} again.
        </Text>

        <UserDisplay member={member} />
      </Dialog>
    </>
  );
}
